/**
 * Projelerim.
 *
 * A search box, four filters and a sort, all applied in memory to one request.
 * That is a deliberate shape: the list endpoint now carries each project's
 * session count, file count, real last activity, latest coverage verdict and
 * latest reconstruction status, so every filter here reads a field that
 * arrived with the page. No filter costs a request, and adding a project does
 * not add a round trip.
 *
 * Two things are absent on purpose. There is no project "durum" filter -
 * `Project.status` is an editorial draft/active/archived that nothing in the
 * product sets, and capture progress is not a list field. And nothing is
 * persisted: filters live for as long as the screen does. Writing them to
 * `localStorage` would mean a user who filtered to "Medya yok" last week comes
 * back to a library that looks empty, with the reason off-screen.
 *
 * The controls come from `components/FilterControls`: one search field, four
 * filter triggers and a sort, sharing a height, a surface, a radius and a
 * focus treatment. They replaced native `<select>`s, whose behaviour was right
 * and whose appearance could not be made to belong here - a select's popup is
 * drawn by the operating system and accepts no styling, no checkmark and no
 * section heading. The keyboard and screen-reader work the platform was doing
 * for free is done explicitly there rather than dropped.
 *
 * A trigger shows its own value without being opened, so the toolbar *is* the
 * active-filter display; an active one grows a × that clears only itself.
 */

import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listProjects } from "@/api/client";
import type { ProjectListItem } from "@/api/types";
import { ErrorState } from "@/components/ErrorState";
import {
  BooleanFilter,
  FilterSearch,
  FilterSelect,
  type FilterOption,
} from "@/components/FilterControls";
import { IconPlus } from "@/components/icons";
import { PageHeader } from "@/components/product";
import { Button, EmptyState, Loading } from "@/components/ui";
import { CATEGORY_OF_SUBJECT, productCategoryLabel, subjectTypeLabel } from "@/i18n/terms";
import { useAsync } from "@/lib/useAsync";
import { paths } from "@/app/paths";
import { ProjectCard } from "./ProjectCard";
import {
  applyFilters,
  COVERAGE_PRESENCE_LABELS,
  DEFAULT_FILTERS,
  isFiltered,
  PRESENCE_LABELS,
  resultCountLabel,
  SORT_LABELS,
  type PresenceFilter,
  type ProjectFilters,
  type SortKey,
} from "./filters";
import "./projects.css";

const EMPTY: readonly ProjectListItem[] = [];

/** The three presence states, in the order they are offered. */
const presenceOptions = (
  labels: Record<PresenceFilter, string>,
): FilterOption<PresenceFilter>[] =>
  (["all", "yes", "no"] as const).map((value) => ({ value, label: labels[value] }));

export function ProjectListScreen() {
  const navigate = useNavigate();
  const fetcher = useCallback(
    (signal: AbortSignal) => listProjects({ limit: 200 }, { signal }),
    [],
  );
  const { state, reload } = useAsync(fetcher);

  const [filters, setFilters] = useState<ProjectFilters>(DEFAULT_FILTERS);
  const set = <K extends keyof ProjectFilters>(key: K, value: ProjectFilters[K]) =>
    setFilters((current) => ({ ...current, [key]: value }));

  /**
   * A stable empty array, not a fresh `[]` per render.
   *
   * This is the same bug shape this repository shipped once before: a literal
   * in a conditional becomes a new identity every render, which invalidates
   * every `useMemo` that depends on it and re-sorts and re-filters the whole
   * list on each pass. ESLint flags it; the fix is one constant.
   */
  const projects = state.status === "success" ? state.data : EMPTY;

  // Only categories actually present in this library. A person with three head
  // projects should not be offered a menu of choices that return nothing.
  const availableCategories = useMemo(
    () =>
      Array.from(
        new Set(projects.map((project) => CATEGORY_OF_SUBJECT[project.subject_type])),
      ),
    [projects],
  );

  // Only subject types present in this library, for the same reason as above.
  const availableSubtypes = useMemo(
    () => Array.from(new Set(projects.map((project) => project.subject_type))),
    [projects],
  );

  // Counted from the evaluated workflow that arrived with the list. No request
  // and no client-side rule: `attention` is non-empty only when the backend
  // measured something.
  const attentionCount = useMemo(
    () => projects.filter((project) => (project.workflow?.attention.length ?? 0) > 0).length,
    [projects],
  );

  const visible = useMemo(() => applyFilters(projects, filters), [projects, filters]);
  const filtered = isFiltered(filters);

  return (
    <div className="stack">
      <PageHeader
        title="Projelerim"
        actions={
          <Button variant="primary" onClick={() => navigate(paths.newProject())}>
            <IconPlus size={16} /> Yeni Proje
          </Button>
        }
      />

      {state.status === "loading" && state.data === null && <Loading />}
      {state.status === "error" && <ErrorState error={state.error} onRetry={reload} />}

      {state.status === "success" && projects.length === 0 && (
        <EmptyState
          title="Henüz bir projeniz yok."
          description="İlk projenizi oluşturarak çekim yüklemeye başlayın."
          action={
            <Button variant="primary" onClick={() => navigate(paths.newProject())}>
              Yeni Proje Oluştur
            </Button>
          }
        />
      )}

      {state.status === "success" && projects.length > 0 && (
        <>
          <div className="library__toolbar">
            <FilterSearch
              value={filters.query}
              onChange={(value) => set("query", value)}
              placeholder="Proje adı veya açıklamasında ara…"
              label="Proje ara"
            />

            <div className="library__filters">
              {availableCategories.length > 1 && (
                <FilterSelect
                  label="Tür"
                  value={filters.category}
                  defaultValue="all"
                  onChange={(value) => set("category", value)}
                  options={[
                    { value: "all", label: "Tümü" },
                    ...availableCategories.map((value) => ({
                      value,
                      label: productCategoryLabel(value),
                    })),
                  ]}
                />
              )}

              {/*
                The six stored subject types, offered under their product
                names - never the enum values. Only the ones actually present
                appear, for the same reason the category filter does that: a
                menu of choices that return nothing is a worse menu.
              */}
              {availableSubtypes.length > 1 && (
                <FilterSelect
                  label="Alt tür"
                  value={filters.subtype}
                  defaultValue="all"
                  onChange={(value) => set("subtype", value)}
                  options={[
                    { value: "all", label: "Tümü" },
                    ...availableSubtypes.map((value) => ({
                      value,
                      label: subjectTypeLabel(value),
                    })),
                  ]}
                />
              )}

              {/*
                Three states each, so a segmented control rather than a menu:
                every option is visible at once and changing it is one click.
              */}
              {projects.length > 3 && <BooleanFilter
                  label="Medya"
                  value={filters.media}
                  defaultValue="all"
                  onChange={(value) => set("media", value)}
                  options={presenceOptions(PRESENCE_LABELS)}
                />}

              {projects.length > 3 && <BooleanFilter
                label="Kapsama"
                value={filters.coverage}
                defaultValue="all"
                onChange={(value) => set("coverage", value)}
                options={presenceOptions(COVERAGE_PRESENCE_LABELS)}
              />}

              {projects.length > 3 && <BooleanFilter
                label="Rekonstrüksiyon"
                value={filters.reconstruction}
                defaultValue="all"
                onChange={(value) => set("reconstruction", value)}
                options={presenceOptions(PRESENCE_LABELS)}
              />}

              {/*
                Sorting is a list, and it never shows a clear affordance:
                `defaultValue` is the current value, so `active` is always
                false. Reordering hides nothing, so "clear" would be a lie.
              */}
              <FilterSelect
                label="Sırala"
                value={filters.sort}
                defaultValue={filters.sort}
                onChange={(value) => set("sort", value)}
                options={(Object.keys(SORT_LABELS) as SortKey[]).map((value) => ({
                  value,
                  label: SORT_LABELS[value],
                }))}
              />

              {/*
                A plain toggle rather than a three-state control: "projects
                with nothing wrong" is not a question anybody asks, and
                offering it would imply the absence of a flag is itself a
                state. Shown only when the loaded page actually contains one,
                so the control never returns an empty list on its first press.
              */}
              {projects.length > 3 && attentionCount > 0 && (
                <button
                  type="button"
                  className={`library__toggle${filters.attention ? " library__toggle--on" : ""}`}
                  aria-pressed={filters.attention}
                  onClick={() => set("attention", !filters.attention)}
                >
                  Dikkat gerekiyor ({attentionCount})
                </button>
              )}

              {filtered && (
                <button
                  type="button"
                  className="library__clear"
                  onClick={() => setFilters((current) => ({ ...DEFAULT_FILTERS, sort: current.sort }))}
                >
                  Filtreleri Temizle
                </button>
              )}

              {/*
                The count sits at the end of the control row rather than
                floating at the far right of the page, so it reads as the
                toolbar's own output instead of an unrelated caption.
              */}
              <span className="library__count">
                {resultCountLabel(visible.length, projects.length)}
              </span>
            </div>
          </div>

          {visible.length === 0 ? (
            <EmptyState
              title="Bu filtrelere uyan proje bulunamadı."
              description="Aramayı veya filtreleri değiştirerek yeniden deneyin."
              action={
                <Button
                  onClick={() =>
                    setFilters((current) => ({ ...DEFAULT_FILTERS, sort: current.sort }))
                  }
                >
                  Filtreleri temizle
                </Button>
              }
            />
          ) : (
            <div className="library__grid">
              {visible.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onDeleted={reload}
                  // Re-read from the server rather than patching the row in
                  // place: the edit may change `last_activity_at` ordering,
                  // and a locally spliced list would sort differently from
                  // the one the next visitor sees.
                  onUpdated={reload}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
